import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X, Hash, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Admin modal to configure the document numbering template(s).
 * Phase 1 of Document Control enhancements — configurable + atomic numbering.
 *
 * Backend:
 *   GET  /api/documents/number-config
 *   GET  /api/documents/number-config/preview?template=&document_type=
 *   PUT  /api/documents/number-config   { document_type, template, seq_padding }
 */
const DOCUMENT_TYPES = ['SOP', 'Work_Instruction', 'Policy', 'Form', 'Checklist', 'Manual', 'SDS', 'Other'];
const API = `${process.env.REACT_APP_BACKEND_URL}/api/documents`;

// Plain-language description + example for each token, shown to help the user
// understand what each placeholder becomes in a real document number.
const TOKEN_INFO = {
  '{TYPE}': { label: 'Document type code', example: 'SOP' },
  '{YYYY}': { label: 'Year (4 digits)', example: '2026' },
  '{YY}': { label: 'Year (2 digits)', example: '26' },
  '{MM}': { label: 'Month (2 digits)', example: '06' },
  '{SEQ}': { label: 'Running number (padded to the digits set below)', example: '0001' },
  '{SEQ:04d}': { label: 'Running number, fixed to 4 digits', example: '0001' },
  '{SITE}': { label: 'Site code', example: 'SITE1' },
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DocumentNumberingSettings = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  // The document_type being edited; '__default__' is the tenant fallback.
  const [docType, setDocType] = useState('__default__');
  const [template, setTemplate] = useState('DOC-{YYYY}-{SEQ:04d}');
  const [seqPadding, setSeqPadding] = useState(4);
  const [preview, setPreview] = useState('');
  // Map of document_type -> {template, seq_padding} loaded from the backend.
  const [configs, setConfigs] = useState({});

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/number-config`, authHeaders());
      const byType = {};
      (res.data.configs || []).forEach((c) => {
        byType[c.document_type] = { template: c.template, seq_padding: c.seq_padding };
      });
      setConfigs(byType);
      setTokens(res.data.tokens || []);
      const def = byType['__default__'] || { template: 'DOC-{YYYY}-{SEQ:04d}', seq_padding: 4 };
      setDocType('__default__');
      setTemplate(def.template);
      setSeqPadding(def.seq_padding);
    } catch (e) {
      setError('Failed to load numbering configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadConfigs();
  }, [open, loadConfigs]);

  // When switching the selected type, load its existing template (or fall back
  // to the default template as a starting point).
  const selectType = (type) => {
    setSavedMsg('');
    setDocType(type);
    const existing = configs[type] || configs['__default__'] || { template: 'DOC-{YYYY}-{SEQ:04d}', seq_padding: 4 };
    setTemplate(existing.template);
    setSeqPadding(existing.seq_padding);
  };

  // Debounced live preview from the server (authoritative renderer).
  useEffect(() => {
    if (!open || !template) { setPreview(''); return; }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ template });
        if (docType !== '__default__') params.append('document_type', docType);
        if (seqPadding) params.append('seq_padding', String(seqPadding));
        const res = await axios.get(`${API}/number-config/preview?${params.toString()}`, authHeaders());
        setPreview(res.data.preview);
      } catch (e) {
        setPreview('(invalid template)');
      }
    }, 300);
    return () => clearTimeout(t);
  }, [template, docType, seqPadding, open]);

  // Number format is mandatory and must contain a running-number token.
  const trimmedTemplate = (template || '').trim();
  const hasSeqToken = /\{SEQ(:\d+d?)?\}/i.test(trimmedTemplate);
  const templateError = !trimmedTemplate
    ? 'Enter a number format.'
    : (!hasSeqToken ? 'Add a {SEQ} token so every document gets its own number.' : '');

  const save = async () => {
    if (templateError) { setError(templateError); return; }
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const res = await axios.put(
        `${API}/number-config`,
        { document_type: docType, template, seq_padding: Number(seqPadding) },
        authHeaders()
      );
      setConfigs((prev) => ({ ...prev, [docType]: { template: res.data.template, seq_padding: res.data.seq_padding } }));
      setSavedMsg('Saved.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Document Numbering</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Scope selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Applies to</label>
              <select
                value={docType}
                onChange={(e) => selectType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="__default__">Default (all document types)</option>
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replace('_', ' ')}{configs[t] ? ' — customised' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Set a tenant-wide default, or override the format for a specific document type.
              </p>
            </div>

            {/* Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number format <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={template}
                onChange={(e) => { setTemplate(e.target.value); setSavedMsg(''); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm font-mono ${templateError ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'}`}
                placeholder="DOC-{YYYY}-{SEQ:04d}"
              />
              {templateError && (
                <p className="text-xs text-red-600 mt-1">{templateError}</p>
              )}

              {/* Click a tag to add it; hover for what it becomes. */}
              <p className="text-xs text-gray-600 mt-2 mb-1">Click a tag to add it to the format:</p>
              <div className="flex flex-wrap gap-1.5">
                {tokens.map((tok) => {
                  const info = TOKEN_INFO[tok];
                  return (
                    <button
                      key={tok}
                      type="button"
                      title={info ? `${info.label} — e.g. ${info.example}` : tok}
                      onClick={() => { setTemplate((prev) => prev + tok); setSavedMsg(''); }}
                      className="text-xs font-mono bg-slate-100 hover:bg-slate-200 text-slate-700 rounded px-2 py-0.5"
                    >
                      {tok}
                    </button>
                  );
                })}
              </div>

              {/* Token reference with examples */}
              <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-xs font-medium text-gray-600 mb-1.5">What each tag means</p>
                <ul className="space-y-0.5">
                  {tokens.filter((tok) => TOKEN_INFO[tok]).map((tok) => (
                    <li key={tok} className="text-xs text-gray-600 flex gap-2">
                      <code className="font-mono text-slate-700 min-w-[72px]">{tok}</code>
                      <span>{TOKEN_INFO[tok].label} <span className="text-gray-400">(e.g. {TOKEN_INFO[tok].example})</span></span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Tip: include <code className="font-mono">{'{SEQ}'}</code> for the running number. Add{' '}
                <code className="font-mono">{'{YYYY}'}</code> or <code className="font-mono">{'{YY}'}</code> to start
                numbering over each year, or <code className="font-mono">{'{MM}'}</code> to start over each month.
              </p>
            </div>

            {/* Padding */}
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sequence digits</label>
              <input
                type="number"
                min={1}
                max={12}
                value={seqPadding}
                onChange={(e) => setSeqPadding(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">How many digits the running number is padded to — e.g. 4 shows as <code className="font-mono">0001</code>. Applies to <code className="font-mono">{'{SEQ}'}</code> (not <code className="font-mono">{'{SEQ:04d}'}</code>, which already sets its own width).</p>
            </div>

            {/* Preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 mb-1">Next-number preview</div>
              <div className="text-lg font-mono font-bold text-slate-800">{preview || '—'}</div>
            </div>

            {error && <div className="text-sm text-red-600">{error}</div>}
            {savedMsg && (
              <div className="flex items-center gap-1 text-sm text-green-700">
                <Check className="w-4 h-4" /> {savedMsg}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving || loading || !!templateError} className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentNumberingSettings;
